import type { Team, TeamSize } from '../types';

export function attemptTransform(data: Record<string, unknown>): Record<string, unknown> {
  const { _participation, _teamScoring, ...rest } = data;
  if (_participation === 'team' && _teamScoring === 'single_result') {
    const teamRest = { ...rest };
    delete teamRest.student_id;
    return teamRest;
  }
  return rest;
}

export const attemptAnnotationTransform = (data: Record<string, unknown>) => ({
  notes: data.notes,
  correction_reason: data.correction_reason,
  ...(data.expected_version !== undefined || data.record_version !== undefined
    ? { expected_version: data.expected_version ?? data.record_version }
    : {}),
});

export function attemptCreateSuccessMessage(data: unknown): string {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return 'Attempt recorded';
  const response = data as Record<string, unknown>;
  if (typeof response.team_attempt_group !== 'string' || !Array.isArray(response.attempts)) {
    return 'Attempt recorded';
  }
  return `Recorded for ${response.attempts.length} team members`;
}

export function teamSizeHelperText(sizeRule: TeamSize | undefined): string {
  if (!sizeRule) return '';
  if ('exact' in sizeRule) return `Requires exactly ${sizeRule.exact} members.`;
  return `Requires ${sizeRule.min}–${sizeRule.max} members.`;
}

export function teamSizeViolation(team: Team | null, sizeRule: TeamSize | undefined): string {
  if (!team || !sizeRule) return '';
  const count = team.member_ids?.length ?? 0;
  if ('exact' in sizeRule && count !== sizeRule.exact) {
    return `"${team.name}" has ${count} member${count !== 1 ? 's' : ''} but this event requires exactly ${sizeRule.exact}.`;
  }
  if ('min' in sizeRule && (count < sizeRule.min || count > sizeRule.max)) {
    return `"${team.name}" has ${count} member${count !== 1 ? 's' : ''} but this event requires ${sizeRule.min}–${sizeRule.max}.`;
  }
  return '';
}

export function teamEligible(team: Team, sizeRule: TeamSize | undefined): boolean {
  if (!sizeRule) return true;
  const count = team.member_ids?.length ?? 0;
  if ('exact' in sizeRule) return count === sizeRule.exact;
  return count >= sizeRule.min && count <= sizeRule.max;
}

export function teamOptionText(team: Team, sizeRule: TeamSize | undefined): string {
  const count = team.member_ids?.length ?? 0;
  const memberLabel = `${count} member${count !== 1 ? 's' : ''}`;
  if (!sizeRule) return `${team.name} (${memberLabel})`;
  if ('exact' in sizeRule) {
    return count === sizeRule.exact
      ? `${team.name} (${memberLabel})`
      : `${team.name} (${memberLabel} — event requires exactly ${sizeRule.exact})`;
  }
  const eligible = count >= sizeRule.min && count <= sizeRule.max;
  return eligible
    ? `${team.name} (${memberLabel})`
    : `${team.name} (${memberLabel} — event requires ${sizeRule.min}–${sizeRule.max})`;
}
